/**
 * BooleanCell — a `type: "boolean"` column as a checkbox rather than a word.
 *
 * With no renderer AG Grid prints the value, so a boolean column was a stack of
 * the words `true` and `false`. Both start with a letter of the same height and
 * differ near the end, so telling one row from the next means *reading* every
 * cell — which is the one thing a screener column is not for. A box either
 * carries a tick or does not, and the difference is visible without focusing on
 * it.
 *
 * **Three states, not two.** A missing value renders as nothing at all: an empty
 * cell says "no answer", where an unticked box says "answered, and the answer is
 * no". Collapsing those two is the failure this renderer could most easily
 * introduce, because a screen full of unticked boxes looks the same either way
 * and nothing on screen would say which it is.
 *
 * Not an `<input type="checkbox">`. Nothing in the screener is editable, a real
 * checkbox invites a click that does nothing, and it takes the platform's own
 * accent colour rather than the workspace theme's (D13).
 */

import type { ScreenerValue } from "@qufolio/transport";

/**
 * What the grid holds for a boolean field, widened on purpose.
 *
 * The column's Arrow type says boolean, so the decoder hands us `true`/`false`
 * — but a **computed** column is evaluated in the browser from an expression
 * whose comparison may return a number, and a server that ever spells one as
 * `"true"` should light the same box rather than falling through to a word. The
 * coercion is deliberately narrow: only the two spellings and the two numbers
 * that mean something, so a stray string is `null` and prints nothing rather
 * than becoming a confident, wrong tick.
 */
export function asBoolean(value: unknown): boolean | null {
  if (typeof value === "boolean") return value;
  if (value === 1) return true;
  if (value === 0) return false;
  if (value === "true") return true;
  if (value === "false") return false;
  return null;
}

export interface BooleanCellProps {
  value: ScreenerValue | unknown;
}

export function BooleanCell({ value }: BooleanCellProps) {
  const state = asBoolean(value);
  if (state === null) return null;

  return (
    <span
      // `grid` + `placeItems` rather than a line-height guess: the row height is
      // a theme variable and the box has to stay centred when it changes.
      style={{ display: "grid", placeItems: "center", height: "100%" }}
      // The word is still what a screen reader announces, and still what a
      // copied cell carries — the box is a way of *showing* the value, not a
      // replacement for it.
      role="img"
      aria-label={state ? "true" : "false"}
      title={state ? "true" : "false"}
    >
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <rect
          x="1.5"
          y="1.5"
          width="13"
          height="13"
          rx="3"
          // Ticked is filled, unticked is an outline. Weight rather than hue
          // carries it, so the two states are still distinguishable to anyone
          // who cannot separate the colours — and so the column does not become
          // a field of green and red, which in this app means up and down.
          fill={state ? "var(--qf-accent)" : "none"}
          stroke={state ? "var(--qf-accent)" : "var(--qf-text-muted)"}
          strokeWidth="1.4"
        />
        {state && (
          <polyline
            points="4.4,8.2 6.9,10.7 11.6,5.6"
            // The tick is cut out of the fill in the page's own background
            // colour, not white: the accent is a dark blue on the White theme
            // and a light one on the dark themes, so a white tick disappears
            // into exactly one of them.
            stroke="var(--qf-background)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </svg>
    </span>
  );
}
